import React, { useEffect, useRef, useState } from 'react';
import { STATS } from '../data/mock';
import { usePublicSettings } from '../hooks/usePublicSettings';
import { InlineText } from './InlineEditable';

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && setInView(true), { threshold: 0.2 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

function Counter({ value, suffix = '', duration = 1600 }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);
  const formatted = n.toLocaleString('tr-TR');
  return <span ref={ref} className="tabular">{formatted}{suffix}</span>;
}

const FALLBACK_TITLE = 'Rakamlarla <em>değişim</em><br />hikâyelerimiz.';
const FALLBACK_TEXT = "1992'den bu yana Türkiye'nin her köşesindeki çocuk ve gencimizle buluştuk. Her bir rakam; bir hayal, bir diploma, bir gelecektir.";

export default function ImpactStats() {
  const s = usePublicSettings();

  const stats = [1, 2, 3, 4].map((i, idx) => {
    const v = s[`home_stat_${i}_value`];
    const fallback = STATS[idx] || { value: 0, suffix: '', label: '', sub: '' };
    return {
      value: v ? Number(String(v).replace(/[^0-9.-]/g, '')) || 0 : fallback.value,
      suffix: s[`home_stat_${i}_suffix`] ?? fallback.suffix,
      label: s[`home_stat_${i}_label`] || fallback.label,
      sub: s[`home_stat_${i}_sub`] || fallback.sub,
    };
  });

  return (
    <section className="relative bg-ink-600 text-cream overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 25% 25%, #C9A96E 0, transparent 60%), radial-gradient(circle at 75% 75%, #E8826F 0, transparent 55%)' }} />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-5">
            <InlineText
              settingKey="home_impact_kicker"
              fallback="Etkimiz"
              as="div"
              className="text-[11px] tracking-[0.3em] uppercase text-gold font-semibold"
            />
            <InlineText
              settingKey="home_impact_title"
              fallback={FALLBACK_TITLE}
              as="h2"
              html
              multiline
              className="home-impact-title font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]"
            />
          </div>
          <div className="lg:col-span-7 text-cream/70 text-base md:text-lg leading-relaxed">
            <InlineText
              settingKey="home_impact_text"
              fallback={FALLBACK_TEXT}
              as="p"
              multiline
            />
          </div>
        </div>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-px bg-cream/10 rounded-3xl overflow-hidden border border-cream/10">
          {stats.map((it, i) => {
            const n = i + 1;
            return (
              <div key={i} className="bg-ink-600 p-8 md:p-10 group hover:bg-ink-500 transition-colors">
                <div className="font-display font-semibold text-4xl md:text-6xl lg:text-7xl leading-none text-cream flex items-baseline">
                  <Counter value={it.value} suffix={it.suffix} />
                  {/* invisible admin-click zone for editing the value/suffix */}
                  <InlineText settingKey={`home_stat_${n}_value`} fallback={String(it.value)} as="span" className="sr-only" />
                </div>
                <InlineText settingKey={`home_stat_${n}_label`} fallback={it.label} as="div" className="mt-4 text-cream font-medium text-base" />
                <InlineText settingKey={`home_stat_${n}_sub`} fallback={it.sub} as="div" className="text-cream/50 text-xs mt-1" />
                <div className="mt-6 h-px bg-gold/0 group-hover:bg-gold transition-colors" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
